import { getReferralRewards, isReferralEligible } from '@/apis/refer-a-friend';
import { getEffectiveCurrency } from '@/utils/currency';
import { logger } from '@/utils/log';
import {
  isZoomOneBusinessPlan,
  isZoomOneProPlan,
  BILLING_CYCLE,
  isZoomOneBusinessPlusPlan,
  isZoomOneProPlusPlan,
} from '@/common/constants';
import { checkIfPaidToPaidFlow } from '@/views/checkout/payment/helper/util';
import { isEmpty } from 'lodash';

export const REFERRAL_USER_TYPE = 1
export const REFERRAL_REWARD_TYPE_PERCENTAGE = 1
// export const REFERRAL_REWARD_TYPE_AMOUNT = 2

export const REFERRAL_INELIGIBILITY_REASON = {
  PLAN_NOT_SUPPORTED: 'plan_not_supported',
  FREE_TRIAL_NOT_SUPPORTED: 'free_trial_not_supported',
}

export const REFERRAL_ACTION_TYPES = {
  initReferAFriendState: 'initReferAFriendState',
  fetchReferralRewards: 'fetchReferralRewards',
  checkReferralEligibility: 'checkReferralEligibility',
};

const state = {
  referralCode: sessionStorage.getItem('refCode'),
  isEligibleForReferAFriendDiscount: false,
  referAFriendRewardValue: 0,
  referAFriendRewardType: 0,
  ineligibilityReason: null,
};

const getters = {
  isEligibleForReferAFriendDiscount: (state, getters) => {
    if (checkIfPaidToPaidFlow(window.$context.flow)) {
      return false;
    }
    if (getters.couponState?.couponCode) {
      return false;
    }
    // Check if any selected plan is annual billed monthly
    const hasAnnualBilledMonthlyPlan = getters.getSelectedPlans?.some(
      plan => plan.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY
    );
    if (hasAnnualBilledMonthlyPlan) {
      return false;
    }
    return state.isEligibleForReferAFriendDiscount
  },
  referAFriendRewardValue: (state, getters) => {
    if (!getters.isEligibleForReferAFriendDiscount) {
      return 0;
    }
    return state.referAFriendRewardValue;
  },
  referAFriendRewardType: (state) => state.referAFriendRewardType,
  referralCode: (state, getters) => {
    if (!getters.isEligibleForReferAFriendDiscount) {
      return undefined;
    }
    return state.referralCode
  },
  referAFriendIneligibilityReason: (state) => state.ineligibilityReason,
}

const mutations = {
  setRewardValue(state, value) {
    state.referAFriendRewardValue = value;
  },
  setRewardType(state, value) {
    state.referAFriendRewardType = value;
  },
  setIsEligibleForReferAFriendDiscount(state, value) {
    state.isEligibleForReferAFriendDiscount = value;
  },
  setIneligibilityReason(state, value) {
    state.ineligibilityReason = value;
  }
}

const actions = {
  async [REFERRAL_ACTION_TYPES.checkReferralEligibility]({ commit, state, getters, dispatch }, countryOverride = null) {
    // Reset the ineligibilityReason
    commit('setIneligibilityReason', null);

    // Check that refCode is set and not p2p flow
    if (!state.referralCode || checkIfPaidToPaidFlow(window.$context.flow)) {
      commit('setIsEligibleForReferAFriendDiscount', false);
      return;
    }

    try {

      // Check account, currency and country
      const currency = getEffectiveCurrency();
      const country = countryOverride || getters.addressInfo?.country;
      const eligibilityResponse = await isReferralEligible(state.referralCode, country, currency);
      if(eligibilityResponse?.result !== true) {
        commit('setIsEligibleForReferAFriendDiscount', false);
        if (!isEmpty(getters.freeTrialPlans)) {
          commit('setIneligibilityReason', REFERRAL_INELIGIBILITY_REASON.FREE_TRIAL_NOT_SUPPORTED);
        }
        return;
      }

      // Check that "Zoom Workplace Pro" is in the cart
      const selectedPlansMap = getters.getSelectedPlansMap;
      const hasProOrBusinessPlan = selectedPlansMap && Object.values(selectedPlansMap).some((p) =>
        isZoomOneProPlan(p) || isZoomOneProPlusPlan(p) || isZoomOneBusinessPlan(p) || isZoomOneBusinessPlusPlan(p)
      );

      if (!hasProOrBusinessPlan) {
        commit('setIsEligibleForReferAFriendDiscount', false);
        commit('setIneligibilityReason', REFERRAL_INELIGIBILITY_REASON.PLAN_NOT_SUPPORTED);
        return;
      }

      // Current user is eligible for Referral Program
      commit('setIsEligibleForReferAFriendDiscount', true);

      // Fetch referral rewards
      await dispatch(REFERRAL_ACTION_TYPES.fetchReferralRewards);

    } catch (error) {
      logger.error('Failed to check referral eligibility:', error);
      commit('setIsEligibleForReferAFriendDiscount', false);
    }
  },

  async [REFERRAL_ACTION_TYPES.fetchReferralRewards]({ commit, state }) {
    const isEligibleForReferAFriendDiscount = state.isEligibleForReferAFriendDiscount;

    // Only fetch referral rewards if refCode is present
    if (!isEligibleForReferAFriendDiscount) {
      return;
    }

    try {
      const currency = getEffectiveCurrency();
      const response = await getReferralRewards(currency, REFERRAL_USER_TYPE);
      if (response?.result?.rewardValue !== undefined) {
        commit('setRewardValue', response.result.rewardValue);
        commit('setRewardType', response.result.rewardType);
      }
    } catch (error) {
      logger.error('Failed to fetch referral rewards:', error);
    }
  },
  async [REFERRAL_ACTION_TYPES.initReferAFriendState]({ dispatch }) {
    await dispatch(REFERRAL_ACTION_TYPES.checkReferralEligibility);
  }
}

export default {
  state,
  getters,
  mutations,
  actions
};
